import React, { useState, useEffect } from 'react';
import AuthService from '../../../api/services/AuthService';
import { alertErrorMessage, alertSuccessMessage } from '../../../customComponent/CustomAlertMessage';
import LoaderHelper from '../../../customComponent/Loading/LoaderHelper';
import { apiUrl } from '../../../api/apiConfig/ApiConfig';

const DocumentViewerModal = ({ show, onClose, documentData, user, investment, onRegenerate }) => {
    const [activeTab, setActiveTab] = useState('pdf');
    const [placeholders, setPlaceholders] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [userDownload, setUserDownload] = useState(false);
    const [withAgn, setWithAgn] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    useEffect(() => {
        if (documentData?.placeholders) {
            setPlaceholders(documentData.placeholders);
        }
        if (documentData?.userDownload !== undefined) {
            setUserDownload(documentData.userDownload);
        }
    }, [documentData]);

    const parseDateFromFormat = (dateString) => {
        if (!dateString) return '';
        // Parse "November 10, 2025" format to YYYY-MM-DD
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    if (!show) return null;

    const handlePlaceholderChange = (key, value) => {
        setPlaceholders(prev => ({ ...prev, [key]: value }));
        setHasChanges(true);
    };

    const handleAgreementDateChange = (dateValue) => {
        if (!dateValue) return;
        
        const selectedDate = new Date(dateValue);
        const formattedDate = selectedDate.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
        
        setPlaceholders(prev => ({
            ...prev,
            AGREEMENT_DATE: formattedDate,
            EFFECTIVE_DATE: formattedDate,
            EFFECTIVE_DATE_FORMATTED: formattedDate,
            agreementDate: formattedDate
        }));
        setHasChanges(true);
    };

    const getFieldLabel = (key) => {
        const labels = {
            'NAME': 'Investor Name',
            'AGREEMENT_DATE': 'Agreement Date',
            'AGREEMENT_NUMBER': 'Agreement Number',
            'TRANSACTION_CODE': 'Transaction Code',
            'EFFECTIVE_DATE': 'Effective Date',
            'EFFECTIVE_DATE_FORMATTED': 'Effective Date (Formatted)',
            'COMPANY': 'Company Name',
            'AMOUNT': 'Investment Amount',
            'CURRENCY': 'Currency',
            'ADDRESS': 'Address',
            'CITY': 'City',
            'STATE': 'State',
            'COUNTRY': 'Country',
            'PINCODE': 'Pincode',
            'NOMINEE': 'Nominee Name',
            'RELATION': 'Nominee Relation',
            'AMOUNT_VALUE': 'Amount Value',
            'PAYOUT_VALUE': 'Payout Value',
            'PAYOUT_PERCENT': 'Payout Percentage',
            'PRINCIPAL_AMOUNT': 'Principal Amount',
            'PRINCIPAL_AMOUNT_WORDS': 'Principal Amount (Words)',
            'agreementDate': 'Agreement Date',
            "CDW_NAME": "C/o, D/o, W/o"
        };
        return labels[key] || key.replace(/_/g, ' ');
    };

    const shouldExcludeField = (key) => {
        // Exclude S1 to S12 fields
        const excludePattern = /^S([1-9]|1[0-2])$/;
        return excludePattern.test(key);
    };

    const isFieldDisabled = (key) => {
        return key === 'EFFECTIVE_DATE' || key === 'EFFECTIVE_DATE_FORMATTED';
    };

    const handleDownloadPDF = () => {
        if (!documentData?.pdfUrl) {
            alertErrorMessage("PDF file not available");
            return;
        }
        const pdfUrl = apiUrl + documentData.pdfUrl;
        const fileName = `Agreement_${user?.firstName}_${user?.lastName}_${investment?._id}.pdf`;
        
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleToggleUserDownload = async () => {
        LoaderHelper.loaderStatus(true);
        try {
            const newStatus = !userDownload;
            const response = await AuthService.updateAgreementStatus(investment._id, newStatus);
            if (response.success) {
                setUserDownload(newStatus);
                alertSuccessMessage(`User download ${newStatus ? 'enabled' : 'disabled'} successfully`);
            } else {
                alertErrorMessage(response.message || "Failed to update download status");
            }
        } catch (error) {
            console.error("Failed to update download status:", error);
            alertErrorMessage("Failed to update download status");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };

    const handleDownloadDOCX = () => {
        if (!documentData?.docxUrl) {
            alertErrorMessage("DOCX file not available");
            return;
        }
        const docxUrl = apiUrl + documentData.docxUrl;
        const fileName = `Agreement_${user?.firstName}_${user?.lastName}_${investment?._id}.docx`;
        
        const link = document.createElement('a');
        link.href = docxUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleRegenerate = async () => {
        LoaderHelper.loaderStatus(true);
        try {
            const response = await AuthService.updateAgreementPlaceholders(investment._id, placeholders, withAgn);
            if (response.success) {
                alertSuccessMessage("Agreement regenerated successfully");
                setHasChanges(false);
                setShowConfirmModal(false);
                setWithAgn(false);
                if (onRegenerate) {
                    onRegenerate(response.data);
                }
            } else {
                alertErrorMessage(response.message || "Failed to regenerate agreement");
            }
        } catch (error) {
            console.error("Failed to regenerate agreement:", error);
            alertErrorMessage("Failed to regenerate agreement");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };

    const handleRegenerateClick = () => {
        setShowConfirmModal(true);
    };

    const handleConfirmYes = () => {
        handleRegenerate();
    };

    const handleConfirmNo = () => {
        setShowConfirmModal(false);
        setWithAgn(false);
    };

    const renderPdfViewer = () => {
        if (!documentData?.pdfUrl) {
            return <div className="text-center p-5">PDF not available</div>;
        }

        // Convert local path to server URL
        const pdfUrl = apiUrl + documentData.pdfUrl;

        return (
            <iframe
                src={pdfUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Agreement PDF"
            />
        );
    };

    const renderPlaceholderEditor = () => {
        return (
            <div className="p-4 custom-scrollbar" style={{ height: '100%', overflowY: 'auto' }}>
                <div className="row g-3">
                    {Object.entries(placeholders)
                        .filter(([key]) => !shouldExcludeField(key) && key !== 'S121')
                        .map(([key, value]) => {
                            const isDisabled = isFieldDisabled(key);
                            const isAgreementDate = key === 'AGREEMENT_DATE';
                            
                            return (
                                <div className="col-md-6" key={key}>
                                    <label className="form-label" style={{ color: '#ffdc89', fontWeight: '600', fontSize: '14px' }}>
                                        {getFieldLabel(key)}
                                    </label>
                                    {isAgreementDate ? (
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={parseDateFromFormat(value)}
                                            onChange={(e) => handleAgreementDateChange(e.target.value)}
                                            style={{ 
                                                borderColor: '#ffdc89',
                                                color: '#ffdc89',
                                            }}
                                        />
                                    ) : (
                                        <input
                                            type={typeof value === 'number' ? 'number' : 'text'}
                                            className="form-control"
                                            value={value || ''}
                                            onChange={(e) => handlePlaceholderChange(key, e.target.value)}
                                            disabled={isDisabled}
                                            style={{ 
                                                borderColor: '#ffdc89',
                                                backgroundColor: isDisabled ? 'transparent' : 'transparent',
                                                cursor: isDisabled ? 'not-allowed' : 'text',
                                                color: '#ffdc89'
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        })}
                </div>
                {hasChanges && (
                    <div className="alert alert-warning mt-3" role="alert">
                        <i className="ri-information-line me-2"></i>
                        You have unsaved changes. Click "Regenerate Agreement" to apply changes.
                    </div>
                )}
            </div>
        );
    };

    return (
        <div 
            className="modal fade show" 
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={onClose}
        >
            <div 
                className="modal-dialog modal-xl modal-dialog-centered"
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: '90%', height: '90vh' }}
            >
                <div className="modal-content px-3" style={{ height: '100%' }}>
                    <div className="modal-header" style={{ 
                        backgroundColor: '#393838', 
                        color: '#ffdc89',
                        
                    }}>
                        <h5 className="modal-title">
                            <i className="ri-file-text-line me-2"></i>
                            Agreement - {user?.firstName} {user?.lastName} - {investment?.currency} {parseFloat(investment?.amount || 0).toFixed(2)}
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close btn-close-white" 
                            onClick={onClose}
                        ></button>
                    </div>
                    
                    <div className="modal-body p-0" style={{ height: 'calc(100% - 130px)' }}>
                        {/* Tabs */}
                        <ul className="nav nav-tabs" style={{ borderBottom: '2px solid #ffdc89' }}>
                            <li className="nav-item">
                                <button 
                                    className={`nav-link ${activeTab === 'pdf' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('pdf')}
                                    style={{
                                        color: activeTab === 'pdf' ? '#393838' : '#666',
                                        backgroundColor: activeTab === 'pdf' ? '#ffdc89' : 'transparent',
                                        fontWeight: activeTab === 'pdf' ? 'bold' : 'normal',
                                        border: 'none'
                                    }}
                                >
                                    <i className="ri-file-pdf-line me-2"></i>
                                    PDF Viewer
                                </button>
                            </li>
                            <li className="nav-item">
                                <button 
                                    className={`nav-link ${activeTab === 'placeholders' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('placeholders')}
                                    style={{
                                        color: activeTab === 'placeholders' ? '#393838' : '#666',
                                        backgroundColor: activeTab === 'placeholders' ? '#ffdc89' : 'transparent',
                                        fontWeight: activeTab === 'placeholders' ? 'bold' : 'normal',
                                        border: 'none'
                                    }}
                                >
                                    <i className="ri-edit-box-line me-2"></i>
                                    Change Data
                                </button>
                            </li>
                        </ul>

                        {/* Tab Content */}
                        <div style={{ height: 'calc(100% - 42px)', overflow: 'hidden' }}>
                            {activeTab === 'pdf' && renderPdfViewer()}
                            {activeTab === 'placeholders' && renderPlaceholderEditor()}
                        </div>
                    </div>
                    
                    <div className="modal-footer" style={{ borderTop: '2px solid #ffdc89' }}>
                        <div className="d-flex justify-content-between w-100 align-items-center">
                            <div className="d-flex gap-3 align-items-center">
                                <div className="d-flex gap-2">
                                    <button 
                                        type="button" 
                                        className="btn btn-success btn-sm"
                                        onClick={handleDownloadDOCX}
                                    >
                                        <i className="ri-file-word-line me-1"></i>
                                        Download DOCX
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-danger btn-sm"
                                        onClick={handleDownloadPDF}
                                    >
                                        <i className="ri-file-pdf-line me-1"></i>
                                        Download PDF
                                    </button>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <label className="mb-0" style={{ color: '#ffdc89', fontWeight: '600', fontSize: '14px' }}>
                                        User Download:
                                    </label>
                                    <div className="form-check form-switch">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            role="switch" 
                                            checked={userDownload}
                                            onChange={handleToggleUserDownload}
                                            style={{ 
                                                cursor: 'pointer',
                                                width: '50px',
                                                height: '25px',
                                                backgroundColor: userDownload ? '#28a745' : '#6c757d',
                                                borderColor: userDownload ? '#28a745' : '#6c757d'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                {activeTab === 'placeholders' && (
                                    <button 
                                        type="button" 
                                        className="btn btn-warning"
                                        onClick={handleRegenerateClick}
                                        disabled={!hasChanges}
                                        style={{ backgroundColor: '#ffdc89', borderColor: '#ffdc89', color: '#393838' }}
                                    >
                                        <i className="ri-refresh-line me-1"></i>
                                        Regenerate Agreement
                                    </button>
                                )}
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={onClose}
                                >
                                    <i className="ri-close-line me-1"></i>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div 
                    className="modal fade show" 
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1060 }}
                    onClick={handleConfirmNo}
                >
                    <div 
                        className="modal-dialog modal-dialog-centered px-3"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content px-3" style={{ backgroundColor: '#393838', color: '#ffdc89' }}>
                            <div className="modal-header" style={{ borderBottom: '1px solid #ffdc89' }}>
                                <h5 className="modal-title">
                                    <i className="ri-question-line me-2"></i>
                                    Confirm Regenerate Agreement
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white" 
                                    onClick={handleConfirmNo}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                                    Are you sure you want to regenerate the agreement?
                                </p>
                                <div className="form-check">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="withAgnCheckbox"
                                        checked={withAgn}
                                        onChange={(e) => setWithAgn(e.target.checked)}
                                        style={{ 
                                            cursor: 'pointer',
                                            width: '20px',
                                            height: '20px',
                                            borderColor: '#ffdc89'
                                        }}
                                    />
                                    <label 
                                        className="form-check-label" 
                                        htmlFor="withAgnCheckbox"
                                        style={{ 
                                            cursor: 'pointer', 
                                            marginLeft: '10px',
                                            fontSize: '14px',
                                            color: '#ffdc89'
                                        }}
                                    >
                                        Do you want to regenerate agreement number?
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer" style={{ borderTop: '1px solid #ffdc89' }}>
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={handleConfirmNo}
                                >
                                    <i className="ri-close-line me-1"></i>
                                    No
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-success"
                                    onClick={handleConfirmYes}
                                    style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                                >
                                    <i className="ri-check-line me-1"></i>
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentViewerModal;
